import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FieldsCollectionValidationsService } from '../../../../common/services/fields-collection-validations.service';
import { ValidationsEnum } from '../../../../common/services/validations.enum';
import { XpFieldsCollectionComponent } from '../xp-fields-collection.component';
import { ValidatorData } from '../../../../common/validators/helpers/validator-data.model';
import { DesignerSchemaFieldI } from '../../../models/designer-schema-field.model';

const WINDOW_ARGUMENT_PROPS: Array<keyof DesignerSchemaFieldI> = [
  'offset',
  'default_value',
  'range_before',
  'range_after',
  'ntiles',
];

@Component({
  selector: 'xp-window-arguments-editor',
  template: `
    <div class="window-arguments" [ngClass]="state">
      <div class="window-arguments-wrapper">
        <div *ngIf="isOffsetVisible" class="window-argument">
          <label>offset</label>
          <xp-input
            [ngModel]="value.offset"
            (ngModelChange)="onValueChange($event, 'offset')"
            [state]="offset_state"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()"
            placeholder="-∞"
            [tooltip]="offset_tooltip"
            [isFocused]="focusedProp === 'offset'"
          ></xp-input>
        </div>
        <div *ngIf="isDefaultVisible" class="window-argument">
          <label>default</label>
          <xp-input
            [ngModel]="value.default_value"
            (ngModelChange)="onValueChange($event, 'default_value')"
            [state]="default_value_state"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()"
            placeholder="-∞"
            [tooltip]="default_value_tooltip"
            [isFocused]="focusedProp === 'default_value'"
          ></xp-input>
        </div>
        <div *ngIf="isRangeBeforeVisible" class="window-argument">
          <label>preceding range</label>
          <xp-input
            [ngModel]="value.range_before"
            (ngModelChange)="onValueChange($event, 'range_before')"
            [state]="range_before_state"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()"
            placeholder="-∞"
            [tooltip]="range_before_tooltip"
            [isFocused]="focusedProp === 'range_before'"
          ></xp-input>
        </div>
        <div *ngIf="isRangeAfterVisible" class="window-argument">
          <label>following range</label>
          <xp-input
            [ngModel]="value.range_after"
            (ngModelChange)="onValueChange($event, 'range_after')"
            [state]="range_after_state"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()"
            placeholder="∞"
            [tooltip]="range_after_tooltip"
            [isFocused]="focusedProp === 'range_after'"
          ></xp-input>
        </div>
        <div *ngIf="isNtilesVisible" class="window-argument">
          <label>number of ntiles</label>
          <xp-input
            [ngModel]="value.ntiles"
            (ngModelChange)="onValueChange($event, 'ntiles')"
            [state]="ntiles_state"
            (focus)="onInputFocus()"
            (blur)="onInputBlur()"
            placeholder="∞"
            [tooltip]="ntiles_tooltip"
            [isFocused]="focusedProp === 'ntiles'"
          ></xp-input>
        </div>
      </div>
    </div>
  `,
  providers: [],
})
export class XpWindowArgumentsEditorComponent implements OnChanges {
  @Input() state: string = 'editable';
  @Input() value: DesignerSchemaFieldI;
  @Input() function: string;
  @Input() index: number;
  @Input() focusedProp: keyof DesignerSchemaFieldI;
  @Output() fieldChange = new EventEmitter();
  validate: (...args: any) => ValidatorData;
  isActive: boolean;
  tooltip = '';
  isFirstChange = true;
  FUNCTIONS = {
    LEAD: 'lead',
    LAG: 'lag',
    NTILE: 'ntile',
  };
  isOffsetVisible = false;
  isDefaultVisible = false;
  isRangeBeforeVisible = false;
  isRangeAfterVisible = false;
  isNtilesVisible = false;
  offset_tooltip = '';
  default_value_tooltip = '';
  range_before_tooltip = '';
  range_after_tooltip = '';
  ntiles_tooltip = '';

  constructor(
    private xpFieldsCollectionComponent: XpFieldsCollectionComponent,
    private fieldsCollectionValidationsService: FieldsCollectionValidationsService,
  ) {
    this.validate = this.fieldsCollectionValidationsService.getValidationFunction(
      ValidationsEnum.WINDOW_ARGUMENTS_EDITOR,
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.value) {
      const { currentValue } = changes.value;
      const { previousValue } = changes.value;

      WINDOW_ARGUMENT_PROPS.filter((prop) => !previousValue || currentValue[prop] !== previousValue[prop]).forEach(
        (prop) => {
          this.validityFn(currentValue[prop], prop);
        },
      );
    }

    if (changes.function && changes.function.currentValue) {
      this.initFunction();
      this.setDefaults();
    }
  }

  onInputFocus() {
    this.isActive = true;
    if (this.xpFieldsCollectionComponent) {
      this.xpFieldsCollectionComponent.setRowState(this.index, { isActive: this.isActive });
    }
  }

  onInputBlur() {
    this.isActive = false;
    if (this.xpFieldsCollectionComponent) {
      this.xpFieldsCollectionComponent.setRowState(this.index, { isActive: this.isActive });
    }
  }

  onValueChange(value: string, prop: keyof DesignerSchemaFieldI) {
    if (this.isFirstChange) {
      if (this.xpFieldsCollectionComponent) {
        this.xpFieldsCollectionComponent.setRecordPristineState(this.index, false);
      }
      this.isFirstChange = false;
    }

    this.fieldChange.emit({ value, prop });

    this.validityFn(value, prop);
  }

  validityFn(value: string, prop: string): boolean {
    if (this.xpFieldsCollectionComponent && this.xpFieldsCollectionComponent.isRecordPristine(this.index)) {
      return false;
    }

    const validityObject = this.validate(value, prop, this.function);
    this.setValidity(validityObject, prop);

    return validityObject.valid;
  }

  setValidity(validityObject: Partial<ValidatorData>, prop: string) {
    if (!validityObject.valid) {
      this[`${prop}_state`] = 'error';
      this[`${prop}_tooltip`] = validityObject.message;
    } else {
      this[`${prop}_state`] = 'editable';
      this[`${prop}_tooltip`] = '';
    }
    this.registerError(validityObject.valid, validityObject.message, prop);
    this.setEditorsState();
  }

  setEditorsState() {
    const hasError = Object.keys(this.value).some((key) => this[`${key}_state`] === 'error');
    this.state = hasError ? 'error' : 'editable';
  }

  registerError(isValid: boolean, message: string, prop: string) {
    if (this.xpFieldsCollectionComponent) {
      if (!isValid) {
        this.xpFieldsCollectionComponent.registerError(this.index, message, prop);
      } else {
        this.xpFieldsCollectionComponent.removeError(this.index, prop);
      }
    }
  }

  initFunction() {
    this.isOffsetVisible = false;
    this.isDefaultVisible = false;
    this.isRangeBeforeVisible = false;
    this.isRangeAfterVisible = false;
    this.isNtilesVisible = false;

    switch (this.function) {
      case this.FUNCTIONS.LEAD:
      case this.FUNCTIONS.LAG:
        this.isOffsetVisible = true;
        this.isDefaultVisible = true;
        this.value.range_before = '';
        this.value.range_after = '';
        this.value.ntiles = '';
        this.setValidity({ valid: true }, 'range_before');
        this.setValidity({ valid: true }, 'range_after');
        this.setValidity({ valid: true }, 'ntiles');
        break;
      case this.FUNCTIONS.NTILE:
        this.isRangeBeforeVisible = true;
        this.isRangeAfterVisible = true;
        this.isNtilesVisible = true;
        this.value.offset = '';
        this.value.default_value = '';
        this.setValidity({ valid: true }, 'offset');
        this.setValidity({ valid: true }, 'default_value');
        break;
      default:
        this.isRangeAfterVisible = true;
        this.isRangeBeforeVisible = true;
        this.value.offset = '';
        this.value.default_value = '';
        this.value.ntiles = '';
        this.setValidity({ valid: true }, 'offset');
        this.setValidity({ valid: true }, 'default_value');
        this.setValidity({ valid: true }, 'ntiles');
        break;
    }

    this.setEditorsState();
  }

  setDefaults() {
    // eslint-disable-next-line default-case
    switch (this.function) {
      case this.FUNCTIONS.LEAD:
      case this.FUNCTIONS.LAG:
        this.value.offset = this.value.offset || '1';
        break;
      case this.FUNCTIONS.NTILE:
        this.value.range_before = this.value.range_before || '';
        this.value.range_after = this.value.range_after || '';
        this.value.ntiles = this.value.ntiles || '10';
        break;
    }
  }
}
